import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { crmApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft,
  Building2,
  Loader2,
  FileText,
} from "lucide-react";
import AssessmentReport from "./AssessmentReport";
import WorkflowPackageReview from "./WorkflowPackageReview";
import SourceRegistry from "./SourceRegistry";

export const TenantPortal = ({ session }) => {
  const { tenantId } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeResult, setActiveResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await crmApi.getTenantPortal({ tenant_id: tenantId }, session);
      setPortal(data);

      // Auto-select the best result: prefer detailed over initial
      if (data.results?.length > 0) {
        const detailed = data.results.find(
          (r) => r.session_info?.assessment_type === "detailed" || r.assessment_type === "detailed"
        );
        setActiveResult(detailed || data.results[0]);
      }
    } catch (e) {
      toast.error("Failed to load tenant portal", {
        description: e?.response?.data?.detail || "Try again",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, session]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-foreground/30" />
      </div>
    );
  }

  if (!portal) {
    return (
      <div className="text-center py-20">
        <p className="text-foreground/40">Tenant not found or no data available.</p>
        <Link to="/app/tenants" className="mt-4 inline-block text-sm text-[var(--brand)] hover:underline">
          Back to Tenants
        </Link>
      </div>
    );
  }

  const { tenant, account, contact, results } = portal;

  // If viewing a specific report, show it full screen
  if (activeResult) {
    return (
      <div className="space-y-4" data-testid="tenant-portal">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveResult(null)}
            className="text-foreground/60 hover:text-foreground"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Overview
          </Button>
          <div className="text-sm text-foreground/40">
            {tenant.name} &middot; {activeResult.session_info?.assessment_type === "detailed" ? "Detailed" : "Initial"} Assessment Report
          </div>
        </div>
        <AssessmentReport session={session} resultId={activeResult} onBack={() => setActiveResult(null)} />
      </div>
    );
  }

  // Overview: show tenant info + list of available reports
  return (
    <div className="space-y-8" data-testid="tenant-portal">
      <div className="flex items-center gap-4">
        <Link to="/app/tenants">
          <Button variant="ghost" size="sm" className="text-foreground/60 hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" /> All Tenants
          </Button>
        </Link>
      </div>

      {/* Tenant Header */}
      <Card className="border border-border/60 bg-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
              <Building2 className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{tenant.name}</h1>
              <div className="text-sm text-foreground/50 mt-1">
                {tenant.industry || "—"} &middot; {contact?.full_name || "—"} &middot; {contact?.email || "—"}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assessment Reports */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Assessment Reports</h2>

        {results.length === 0 ? (
          <Card className="border border-border/60 bg-foreground/[0.02]">
            <CardContent className="p-6 text-center text-sm text-foreground/40">
              No completed assessments found for this tenant. Complete an assessment from the Pipeline to generate reports.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {results.map((r) => {
              const isDetailed = (r.session_info?.assessment_type || r.assessment_type) === "detailed";
              const hasPlan = r.implementation_plan || r.generated_report_json;
              const hasShortlist = r.workflow_shortlist?.length > 0;

              return (
                <Card key={r.id} className="border border-border/60 bg-card hover:border-border transition-colors cursor-pointer" data-testid={`report-card-${r.id}`}>
                  <CardContent className="p-5 flex items-center justify-between gap-4" onClick={() => setActiveResult(r)}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isDetailed ? "bg-[var(--brand)]/10 text-[var(--brand)]" : "bg-foreground/5 text-foreground/40"}`}>
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                          {isDetailed ? "Detailed AI Readiness Report" : "Initial AI Readiness Report"}
                        </div>
                        <div className="text-xs text-foreground/40 mt-0.5">
                          Score: {r.overall_readiness_score}/4.0
                          <Badge className={`ml-2 text-[10px] ${r.readiness_band === "Scale Now" ? "bg-emerald-500/10 text-emerald-400" : r.readiness_band === "Pilot Now" ? "bg-[#007a9e]/10 dark:bg-amber-500/10 text-[#007a9e] dark:text-amber-400" : "bg-foreground/5 text-foreground/40"}`}>
                            {r.readiness_band}
                          </Badge>
                          {hasShortlist && <span className="ml-2">&middot; {r.workflow_shortlist.length} workflows</span>}
                          {hasPlan && <span className="ml-2">&middot; Plan generated</span>}
                        </div>
                        {r.session_info?.completed_at && (
                          <div className="text-[11px] text-foreground/25 mt-0.5">
                            Completed {new Date(r.session_info.completed_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button size="sm" className="h-9 rounded-lg bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 font-medium text-xs shrink-0" data-testid={`view-report-btn-${r.id}`}>
                      View Report
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Workflow Package Review (Internal) */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Workflow Package Review</h2>
        <WorkflowPackageReview tenantId={tenantId} />
      </div>

      {/* Approved Knowledge Base / Source Registry */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Approved Knowledge Base</h2>
        <SourceRegistry tenantId={tenantId} />
      </div>
    </div>
  );
};
